import { useCallback, useEffect, useRef, useState } from "react";
import { FileDown, FileText, RefreshCw, Download, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { CopyButton, PageHeader, Section, Spinner } from "../components/AiHtml";
import { LogoUploader } from "../components/Assets";
import { BrandStyleCard } from "../components/BrandStyle";
import { useBrands } from "../context/BrandContext";
import { useAuth } from "../context/AuthContext";
import { api, fileUrl, handleErr } from "../lib/api";

const STATUS = { planning: "Reading your website and brand, planning the week...", images: "Designing your 7 post creatives..." };

const PostCard = ({ p, i }) => (
  <div data-testid={`social-post-${i}`} className="card-n overflow-hidden flex flex-col fade-up" style={{ animationDelay: `${i * 50}ms` }}>
    <div className="aspect-square bg-slate-900 grid place-items-center relative">
      {p.imageStatus === "done" ? <img src={fileUrl("media", p.mediaId)} alt={p.theme} className="w-full h-full object-cover" /> :
        p.imageStatus === "pending" ? <Loader2 className="animate-spin text-orange-500" /> :
          <p className="text-xs text-slate-500 px-4 text-center flex items-center gap-1"><AlertTriangle size={13} /> {p.imageStatus === "no_credits" ? "Not enough credits for image" : "Image failed (refunded)"}</p>}
      <span className="absolute top-2 left-2 badge-o bg-black/60">{p.day} · {p.platform}</span>
    </div>
    <div className="p-4 space-y-2 flex-1 flex flex-col">
      <p className="text-xs text-slate-500 font-mono">{p.date} · {p.best_time}</p>
      <p className="font-semibold text-slate-100">{p.theme}</p>
      <p className="text-sm text-slate-300 whitespace-pre-line flex-1">{p.caption}</p>
      <p className="text-xs text-sky-400">{(p.hashtags || []).map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")}</p>
      <div className="flex gap-2 pt-2">
        <CopyButton testId={`social-copy-${i}`} text={`${p.caption}\n\n${(p.hashtags || []).join(" ")}`} label="Copy post" />
        {p.imageStatus === "done" && <a href={fileUrl("media", p.mediaId)} download className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-slate-700 hover:border-orange-500 hover:text-orange-400"><Download size={13} /> Image</a>}
      </div>
    </div>
  </div>
);

export default function SocialManager() {
  const { active } = useBrands();
  const { config, refresh } = useAuth();
  const [cal, setCal] = useState(undefined);
  const [dl, setDl] = useState("");
  const started = useRef({});
  const timer = useRef(null);
  const cost = config ? config.costs.social_plan + 7 * config.costs.image_1K : 115;

  const generate = useCallback(async (brandId) => {
    try {
      const { data } = await api.post("/social/calendar", { brandId });
      setCal(data);
    } catch (e) { handleErr(e); setCal((c) => c ?? null); } finally { refresh(); }
  }, [refresh]);

  const load = useCallback(async (brandId, auto) => {
    const { data } = await api.get("/social/calendar", { params: { brandId } });
    if (!data && auto && !started.current[brandId]) {
      started.current[brandId] = true;
      return generate(brandId);
    }
    setCal(data);
  }, [generate]);

  const brandId = active?.brandId;
  useEffect(() => { if (brandId) { setCal(undefined); load(brandId, true); } }, [brandId, load]);
  const busy = cal && ["planning", "images"].includes(cal.status);
  useEffect(() => {
    clearInterval(timer.current);
    if (busy) timer.current = setInterval(() => load(cal.brandId), 5000);
    else if (cal?.status === "done") refresh();
    return () => clearInterval(timer.current);
  }, [busy, cal?.brandId, cal?.status, load, refresh]);

  const download = async (format) => {
    setDl(format);
    try {
      const res = await api.get(`/social/calendar/${cal.calendarId}/download`, { params: { format }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `${active.businessName}-social-calendar.${format}`;
      a.click();
    } catch (e) { handleErr(e); } finally { setDl(""); }
  };

  if (!active) return <div className="card-n p-10 text-center text-slate-400">Add a brand first to plan social media.</div>;
  return (
    <div className="space-y-6">
      <PageHeader label="Social Media Manager" title={`${active.businessName} · this week`} desc="Your AI team reads your website, brand details and social accounts, then plans 7 days of posts with on-brand images, captions and hashtags."
        right={cal?.posts?.length > 0 && !busy && (
          <div className="flex flex-wrap gap-2">
            <Button data-testid="social-download-pdf" size="sm" onClick={() => download("pdf")} disabled={!!dl} className="rounded-full"><FileDown size={14} className="mr-1" />{dl === "pdf" ? "Building..." : "PDF"}</Button>
            <Button data-testid="social-download-docx" size="sm" variant="outline" onClick={() => download("docx")} disabled={!!dl} className="rounded-full"><FileText size={14} className="mr-1" />{dl === "docx" ? "Building..." : "Word"}</Button>
            <Button data-testid="social-regenerate" size="sm" variant="outline" onClick={() => window.confirm(`Generate a fresh week? This uses about ${cost} credits.`) && generate(active.brandId)} className="rounded-full"><RefreshCw size={14} className="mr-1" />New week</Button>
          </div>
        )} />
      <LogoUploader />
      <BrandStyleCard />
      {cal === undefined && <div className="card-n"><Spinner label="Loading your calendar..." /></div>}
      {cal === null && (
        <div className="card-n p-8 text-center space-y-3" data-testid="social-empty">
          <p className="text-slate-300">No calendar yet for this brand.</p>
          <Button data-testid="social-generate-btn" onClick={() => generate(active.brandId)} className="rounded-full">Generate this week ({cost} credits)</Button>
        </div>
      )}
      {cal?.status === "failed" && <div data-testid="social-error" className="card-n p-5 text-red-300 flex items-center justify-between gap-3">{cal.error}<Button size="sm" onClick={() => generate(active.brandId)} className="rounded-full">Retry</Button></div>}
      {cal?.status === "planning" && <div className="card-n"><Spinner label={STATUS.planning} /></div>}
      {cal?.posts?.length > 0 && (
        <>
          {cal.strategy && <Section title="Weekly strategy" testId="social-strategy"><p className="text-sm text-slate-300">{cal.strategy}</p>{busy && <p className="text-xs text-orange-400 mt-3 flex items-center gap-2"><Loader2 size={13} className="animate-spin" />{STATUS.images}</p>}</Section>}
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="social-calendar">{cal.posts.map((p, i) => <PostCard key={i} p={p} i={i} />)}</div>
        </>
      )}
    </div>
  );
}
